import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonContent, IonSpinner } from '@ionic/angular/standalone';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PaInputComponent } from 'src/app/shared/ui-kit/pa-input/pa-input.component';
import { PaButtonComponent } from 'src/app/shared/ui-kit/pa-button/pa-button.component';
import { Checkbox } from 'primeng/checkbox';
import { UsersService } from '../../services/users.service';
import { ActivatedRoute } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import {
  UserVacancyForm,
  VacancyFormField,
  UserVacancyResponseData,
  VacancyResponseField,
  UserVacancyFormResponse,
} from '../../models/user.model';
import { PaMultiSelectComponent } from 'src/app/shared/ui-kit/pa-multi-select/pa-multi-select.component';
import { PaInputPhoneComponent } from 'src/app/shared/ui-kit/pa-input-phone/pa-input-phone.component';
import { PaUploadfileComponent } from 'src/app/shared/ui-kit/pa-uploadfile/pa-uploadfile.component';
import { FileDTO } from 'src/app/core/models/files';
import { environment } from 'src/environments/environment';
import { ToolsService } from 'src/app/core/services/tools.service';
import { AnalyticsService } from 'src/app/core/services/analytics.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import { ApiService } from 'src/app/core/services/api.service';
import { PaDataListComponent } from 'src/app/shared/ui-kit/pa-data-list/pa-data-list.component';
import { DataListItem, DataListType } from 'src/app/shared/ui-kit/pa-data-list/models/pa-data-list.model';

@Component({
  selector: 'app-user-vacancy-form',
  templateUrl: './user-vacancy-form.page.html',
  styleUrls: ['./user-vacancy-form.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonContent,
    ReactiveFormsModule,
    PaInputComponent,
    PaButtonComponent,
    PaMultiSelectComponent,
    Checkbox,
    PaInputPhoneComponent,
    PaUploadfileComponent,
    IonSpinner,
    PaDataListComponent,
  ],
})
export class UserVacancyFormPage implements OnInit {
  private readonly api = inject(ApiService);

  vacancyData = signal<UserVacancyForm | null>(null);
  isLoading = signal(true);
  isSubmitted = signal(false);
  isNotAvailable = signal(false);
  vacancyFiles = signal<DataListItem[]>(null);

  isAuthorized = toSignal(this.api.isUser);

  isSaving = signal(false);
  currentUuid = '';

  formFields = computed(() => this.vacancyData()?.source || []);

  pvzs = computed(() => this.vacancyData()?.pvzs || []);

  profileName = computed(() => this.vacancyData()?.profile_name || '');

  introText = computed(() => this.vacancyData()?.intro_text || '');

  outroText = computed(() => this.vacancyData()?.outro_text || '');

  form: FormGroup = new FormGroup({});

  constructor(
    private readonly analyticsService: AnalyticsService,
    private readonly usersService: UsersService,
    private readonly route: ActivatedRoute,
    private readonly title: Title,
    private readonly meta: Meta,
    private readonly toolsService: ToolsService,
    private readonly loadingService: LoadingService,
  ) {}

  ngOnInit(): void {
    this.meta.addTag({ name: 'robots', content: 'noindex, nofollow' });
    this.route.params.subscribe((params) => {
      const uuid = params['uuid'];
      this.currentUuid = uuid;
      this.loadVacancyForm(uuid);
    });
  }

  private loadVacancyForm(uuid: string): void {
    this.isLoading.set(true);

    this.usersService.getPublicVacancyForm(uuid).subscribe({
      next: (response: UserVacancyFormResponse) => {
        if (response.success) {
          this.title.setTitle(`${response.item.profile_name} | Вакансия | ${environment.appTitle}`);
          if (response.item.published === 0) {
            this.isNotAvailable.set(true);
            this.isLoading.set(false);
            return;
          }

          if (response.item.files.length > 0) {
          this.vacancyFiles.set([{
              label: 'Документы для ознакомления',
              type: DataListType.FILES,
              value: response.item.files,
            }]);
          }

          this.vacancyData.set(response.item);
          this.buildForm(response.item.source);
          this.isLoading.set(false);
        } else {
          this.isNotAvailable.set(true);
          this.isLoading.set(false);
        }
      },
      error: () => {
        this.isNotAvailable.set(true);
        this.isLoading.set(false);
      },
    });
  }

  private buildForm(fields: VacancyFormField[]): void {
    const group: { [key: string]: FormControl } = {};

    fields.forEach((field) => {
      const validators = field.required ? [Validators.required] : [];
      if (field.name === 'email') {
        validators.push(Validators.email);
        validators.push(Validators.maxLength(50));
      }
      if (field.name === 'tel') {
        validators.push(Validators.maxLength(25));
        validators.push(Validators.pattern(this.toolsService.phoneRegexp));
      }
      if (field.name === 'telegram') {
        validators.push(Validators.pattern(/^@?[a-zA-Z][a-zA-Z0-9_]{4,31}$/));
        validators.push(Validators.maxLength(32));
      }
      if (field.name === 'fio') {
        validators.push(Validators.maxLength(200));
      }
      const defaultValue = this.getDefaultValue(field.type);
      group[field.name] = new FormControl(defaultValue, validators);
    });

    group['agree'] = new FormControl(null, [Validators.requiredTrue]);

    group['pvzs'] = new FormControl([], this.pvzs().length ? [Validators.required] : []);

    this.form = new FormGroup(group);
  }

  private getDefaultValue(type: string): string | number | boolean | null {
    switch (type) {
      case 'Boolean':
        return false;
      case 'Number':
        return null;
      case 'File':
        return null;
      default:
        return '';
    }
  }

  onSubmit(): void {
    this.analyticsService.phCapture('Вакансия: Кнопка отправки формы нажата', { 'ID Вакансии': this.currentUuid });
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const vacancy = this.vacancyData();
    if (!vacancy) {
      return;
    }

    const responseData = this.prepareResponseData(vacancy);
    this.isSaving.set(true);

    combineLatest([
      this.loadingService.showLoading(),
      this.usersService.savePublicVacancyResponse(this.currentUuid, responseData),
    ]).subscribe({
      next: ([loading, response]) => {
        loading.dismiss();
        if (response.success) {
          this.analyticsService.phCapture('Вакансия: Отклик на вакансию сохранен', { 'ID Вакансии': this.currentUuid });
          this.isSubmitted.set(true);
        }
        this.isSaving.set(false);
      },
      error: (error) => {
        this.analyticsService.phCaputeException('Фиксация ошибки отправки анкеты соискателя', { error, 'ID Вакансии': this.currentUuid });
        this.isSaving.set(false);
      },
    }); 
  }

  private prepareResponseData(vacancy: UserVacancyForm): UserVacancyResponseData {
    const formValue = this.form.value;

    const DEFAULT_FIELDS = new Set(['fio', 'tel', 'email', 'telegram']);

    const source: VacancyResponseField[] = vacancy.source
      .filter((field) => !DEFAULT_FIELDS.has(field.name))
      .reduce((acc, field) => {
        const value = formValue[field.name];

        if (field.type === 'File') {
          const file = formValue[field.name] as FileDTO[];
          if (file) {
            acc.push({ ...field, value: file.map((f) => f.uuid) });
          }
        }

        if (this.isValidValue(value) && field.type !== 'File') {
          if (field.type === 'Number') {
            acc.push({ ...field, value: Number(value) });
          } else {
            acc.push({ ...field, value: value });
          }
        }
        return acc;
      }, [] as VacancyResponseField[]);

    const data: UserVacancyResponseData = { source };

    ['fio', 'tel', 'email', 'telegram', 'pvzs'].forEach((fieldName) => {
      if (formValue[fieldName]) {
        data[fieldName] = formValue[fieldName];
      }
    });

    return data;
  }

  private isValidValue(value: unknown): boolean {
    return value !== undefined && value !== '' && value !== null;
  }

  onFileChange(fieldName: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.form.get(fieldName)?.setValue(file);
    }
  }
}
